import { api } from "@/utils/axiosInterceptor";
import { normalizeExtractedData } from "@/utils/normalizeExtractedData";
import type {
  ClassifyEmailsResponse,
  EmailQuotation,
  EmailQuotationsListResponse,
  ExtractedData,
  FetchEmailsRequest,
  FetchEmailsResponse,
  GmailAuthInitResponse,
  GmailConnectionStatus,
  GmailSettings,
  GmailSettingsUpdate,
  InMemoryEmail,
  QuotationVersion,
  QuotationVersionsResponse,
} from "@/types/gmail";

const GMAIL_BASE = "/gmail";

// ==================== AUTH ====================

export const gmailAuthApi = {
  getStatus: async (): Promise<GmailConnectionStatus> => {
    const response = await api.get<GmailConnectionStatus>(`${GMAIL_BASE}/auth/status`);
    return response.data;
  },

  initiateConnect: async (): Promise<GmailAuthInitResponse> => {
    const response = await api.post<GmailAuthInitResponse>(`${GMAIL_BASE}/auth/connect`);
    return response.data;
  },

  disconnect: async (): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`${GMAIL_BASE}/auth/disconnect`);
    return response.data;
  },
};

// ==================== EMAILS ====================

export const gmailEmailsApi = {
  fetchFromGmail: async (params: FetchEmailsRequest): Promise<FetchEmailsResponse> => {
    const response = await api.post<FetchEmailsResponse>(`${GMAIL_BASE}/emails/fetch`, params);
    return response.data;
  },

  classifyBatch: async (emails: InMemoryEmail[]): Promise<ClassifyEmailsResponse> => {
    const CHUNK_SIZE = 15;
    if (emails.length <= CHUNK_SIZE) {
      const response = await api.post<ClassifyEmailsResponse>(
        `${GMAIL_BASE}/emails/classify`,
        { emails }
      );
      return response.data;
    }

    // Split into chunks and merge results
    const chunks: InMemoryEmail[][] = [];
    for (let i = 0; i < emails.length; i += CHUNK_SIZE) {
      chunks.push(emails.slice(i, i + CHUNK_SIZE));
    }

    const allResults: ClassifyEmailsResponse = { results: [], saved: 0, rejected: 0 };
    for (const chunk of chunks) {
      const response = await api.post<ClassifyEmailsResponse>(
        `${GMAIL_BASE}/emails/classify`,
        { emails: chunk }
      );
      allResults.results.push(...(response.data.results || []));
      allResults.saved += response.data.saved ?? 0;
      allResults.rejected += response.data.rejected ?? 0;
    }
    return allResults;
  },

  list: async (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<EmailQuotationsListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    const response = await api.get<EmailQuotationsListResponse>(
      `${GMAIL_BASE}/emails?${queryParams.toString()}`
    );
    return response.data;
  },

  getById: async (id: string): Promise<EmailQuotation> => {
    const response = await api.get<EmailQuotation>(`${GMAIL_BASE}/emails/${id}`);
    const email = response.data;
    if (email.extractedData) {
      email.extractedData = normalizeExtractedData(email.extractedData) as ExtractedData;
    }
    return email;
  },


  convert: async (id: string, quotationId: string): Promise<EmailQuotation> => {
    const response = await api.post<EmailQuotation>(
      `${GMAIL_BASE}/emails/${id}/convert`,
      { quotationId }
    );
    return response.data;
  },

  reject: async (id: string, reviewNotes?: string): Promise<EmailQuotation> => {
    const response = await api.post<EmailQuotation>(
      `${GMAIL_BASE}/emails/${id}/reject`,
      { reviewNotes }
    );
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`${GMAIL_BASE}/emails/${id}`);
    return response.data;
  },

  approveAndReply: async (id: string, quotationId: string): Promise<EmailQuotation> => {
    const response = await api.post<EmailQuotation>(
      `${GMAIL_BASE}/emails/${id}/approve-reply`,
      { quotationId }
    );
    return response.data;
  },
};

// ==================== SETTINGS ====================

export const gmailSettingsApi = {
  get: async (): Promise<GmailSettings> => {
    const response = await api.get<GmailSettings>(`${GMAIL_BASE}/settings`);
    return response.data;
  },

  update: async (data: GmailSettingsUpdate): Promise<GmailSettings> => {
    const response = await api.put<GmailSettings>(`${GMAIL_BASE}/settings`, data);
    return response.data;
  },
};

// ==================== VERSIONS ====================

export const quotationVersionsApi = {
  getHistory: async (quotationId: string): Promise<QuotationVersionsResponse> => {
    const response = await api.get<QuotationVersionsResponse>(
      `${GMAIL_BASE}/quotations/${quotationId}/versions`
    );
    return response.data;
  },

  getVersion: async (quotationId: string, versionId: string): Promise<QuotationVersion> => {
    const response = await api.get<QuotationVersion>(
      `${GMAIL_BASE}/quotations/${quotationId}/versions/${versionId}`
    );
    return response.data;
  },
};
